import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, PencilLine, Minus, Plus } from 'lucide-react';
import { toast } from 'sonner';

const groupItens = (itens = []) => {
  const g = {};
  for (const it of itens) if (it.material_id) g[it.material_id] = (g[it.material_id] || 0) + (it.quantidade || 0);
  return g;
};

export function EditarQuantidadesDialog({ open, onClose, titulo, itens, endpoint, getMaterialNome, onSaved }) {
  const [qtds, setQtds] = useState({});
  const [original, setOriginal] = useState({});
  const [motivo, setMotivo] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { const g = groupItens(itens); setQtds(g); setOriginal(g); setMotivo(''); }
  }, [open, itens]);

  const setQ = (id, v) => setQtds(p => ({ ...p, [id]: Math.max(1, parseInt(v) || 1) }));
  const changed = Object.keys(qtds).filter(id => qtds[id] !== original[id]);

  const handleSave = async () => {
    if (changed.length === 0) { toast.error('Nenhuma quantidade foi alterada'); return; }
    setSaving(true);
    try {
      const payload = Object.fromEntries(changed.map(id => [id, qtds[id]]));
      const res = await api.put(endpoint, { quantidades: payload, motivo });
      toast.success(res.data.message);
      const n = res.data.notificacoes || {};
      if (n.whatsapp_enviado) toast.success('WhatsApp de revisão enviado ao cliente');
      else if (n.whatsapp_erro) toast.warning(`WhatsApp não enviado: ${n.whatsapp_erro}`);
      if (n.email_enviado) toast.success('Email de revisão enviado ao cliente');
      onSaved?.(res.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao atualizar quantidades');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="dialog-editar-quantidades">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PencilLine className="w-5 h-5 text-blue-600" /> Editar Quantidades</DialogTitle>
        </DialogHeader>
        {titulo && <p className="text-sm text-slate-600 -mt-2">{titulo}</p>}
        <div className="divide-y divide-slate-100 border rounded-lg">
          {Object.keys(qtds).map(id => (
            <div key={id} className={`flex items-center justify-between px-3 py-2 ${qtds[id] !== original[id] ? 'bg-amber-50' : ''}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">{getMaterialNome(id)}</div>
                {qtds[id] !== original[id] && <div className="text-xs text-amber-700">Antes: {original[id]}</div>}
              </div>
              <div className="flex items-center gap-1">
                <Button type="button" variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => setQ(id, qtds[id] - 1)} disabled={qtds[id] <= 1} data-testid={`qtd-minus-${id}`}><Minus className="w-4 h-4" /></Button>
                <Input type="number" min="1" value={qtds[id]} onChange={e => setQ(id, e.target.value)} onFocus={e => e.target.select()} className="w-20 h-8 text-center" data-testid={`qtd-input-${id}`} />
                <Button type="button" variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => setQ(id, qtds[id] + 1)} data-testid={`qtd-plus-${id}`}><Plus className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
        <div>
          <Label>Motivo da correção (opcional)</Label>
          <Input value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Ex: contagem errada na recepção" className="mt-1" data-testid="input-motivo-quantidade" />
        </div>
        <p className="text-xs text-slate-500">A correção atualiza Recepção e Montagem, fica registrada com seu usuário e o cliente recebe uma mensagem informando a revisão.</p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving || changed.length === 0} data-testid="btn-salvar-quantidades">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Salvar {changed.length > 0 ? `(${changed.length})` : ''}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
